import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { TBS_API_URL } from "../../config";
import type { TbsDailyBudget, TbsDailyBudgetInput, TbsDailyBudgetList } from "../../types/tbs/cash";

// Tiendas Biai — the day's cash base. TBS-66's `/v1/cash/daily-budget`, consumed by
// `/gestion/contabilidad`.
//
// Its own slice rather than an endpoint bolted onto `tbsReportsApi`, and the split is the same one
// the backend made: `reports` is read-only and answers in **pesos**, this is read-write and answers
// in **centavos**. One slice holding both would be the single most likely place for the two to be
// confused.

export const tbsCashApi = createApi({
  reducerPath: "tbsCashApi",
  tagTypes: ["TbsDailyBudget"],
  baseQuery: fetchBaseQuery({
    baseUrl: TBS_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      const tenantId = (getState() as RootState).tbsTenant.tenantId;
      if (tenantId) headers.set("X-Tenant-Id", tenantId);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /**
     * The base for one day.
     *
     * **A 404 here is a state, not a failure** — the same arrangement `getCurrentShift` has, and the
     * server says so with the slug `no_daily_budget` rather than `not_found`. A day with no base is
     * what makes the screen offer to open one, so callers branch on `error.status === 404` instead
     * of reporting it.
     */
    getDailyBudget: builder.query<TbsDailyBudget, { date: string }>({
      query: ({ date }) => `/v1/cash/daily-budget/${date}`,
      providesTags: (result, error, { date }) => [{ type: "TbsDailyBudget", id: date }],
    }),

    /**
     * The window, newest first. Not used by the screen — which asks for one day — but declared
     * because the endpoint exists and the next thing anyone wants is "which days have a base".
     */
    listDailyBudgets: builder.query<TbsDailyBudgetList, { from?: string; to?: string } | void>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params?.from) search.set("from", params.from);
        if (params?.to) search.set("to", params.to);
        const query = search.toString();
        return `/v1/cash/daily-budget${query ? `?${query}` : ""}`;
      },
      providesTags: [{ type: "TbsDailyBudget", id: "LIST" }],
    }),

    createDailyBudget: builder.mutation<TbsDailyBudget, TbsDailyBudgetInput & { date: string }>({
      query: (body) => ({ url: "/v1/cash/daily-budget", method: "POST", body }),
      invalidatesTags: (result, error, { date }) => [
        { type: "TbsDailyBudget", id: date },
        { type: "TbsDailyBudget", id: "LIST" },
      ],
    }),

    /**
     * `If-Match` carries the version, and the server refuses a `PUT` without one.
     *
     * It has to travel from the row the form was opened with — looking it up here would fetch the
     * current version and defeat the mechanism entirely, which is the same reason `updateContact`
     * and the four `tbsConfig` writers take it as an argument.
     */
    updateDailyBudget: builder.mutation<
      TbsDailyBudget,
      { date: string; version: number; body: TbsDailyBudgetInput }
    >({
      query: ({ date, version, body }) => ({
        url: `/v1/cash/daily-budget/${date}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: (result, error, { date }) => [
        { type: "TbsDailyBudget", id: date },
        { type: "TbsDailyBudget", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetDailyBudgetQuery,
  useListDailyBudgetsQuery,
  useCreateDailyBudgetMutation,
  useUpdateDailyBudgetMutation,
} = tbsCashApi;
